import { ReactElement, useState } from 'react';
import { Button, Input, LinearLoader } from '@sc/shared-ui';
import { useMutation } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import {
  GQL_MUTATION_RESEND_VERIFICATION_EMAIL,
  GQL_MUTATION_UPDATE_ME,
} from '../utility/queries';
import { DeleteAccountModal } from '../modals/DeleteAccountModal';

const sectionTitle =
  'text-body-medium font-semibold block text-text-low-priority';

export const SettingsAccountTab = (): ReactElement => {
  const { user } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [updateMe, { loading: isLoading }] = useMutation(GQL_MUTATION_UPDATE_ME);
  const [resendVerification, { loading: isResending }] = useMutation(
    GQL_MUTATION_RESEND_VERIFICATION_EMAIL
  );

  const form = useForm({
    defaultValues: {
      name: user.name ?? '',
      email: user.email,
    },
    onSubmit: ({ value }) => {
      const hasEmailChanged = value.email !== user.email;

      void updateMe({
        variables: {
          input: {
            name: value.name,
            email: hasEmailChanged ? value.email : null,
          },
        },
        onCompleted: () => {
          track('Account Details Saved', { emailChanged: hasEmailChanged });

          notify.success(
            hasEmailChanged
              ? 'Account updated — check your inbox to confirm the new email'
              : 'Account updated'
          );
        },
        onError,
      });
    },
  });

  return (
    <>
      {(isLoading || isResending) && <LinearLoader />}
      <div className="p-5 space-y-8 max-w-md">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();

            e.stopPropagation();

            void form.handleSubmit();
          }}
        >
          <form.Field
            name="name"
            children={({ state, handleChange }) => (
              <Input
                label="Name"
                value={state.value}
                required
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <form.Field
            name="email"
            children={({ state, handleChange }) => (
              <Input
                label="Email"
                type="email"
                value={state.value}
                required
                helperText={
                  user.hasPendingEmail
                    ? `A verification email has been sent to ${user.pendingEmail} — check your inbox.`
                    : 'Changing email will trigger a verification email to the new address.'
                }
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <div className="flex items-center justify-between">
            {user.hasPendingEmail ? (
              <Button
                button={{
                  text: 'Resend verification email',
                  type: 'tertiary',
                  size: 'medium',
                  shrink: true,
                  disabled: isResending,
                  onClick: () =>
                    void resendVerification({
                      onCompleted: () => {
                        track('Verification Email Resent', {
                          context: 'settings',
                        });

                        notify.success('Verification email sent');
                      },
                      onError,
                    }),
                }}
              />
            ) : (
              <span />
            )}
            <Button
              button={{
                text: 'Save changes',
                type: 'primary',
                size: 'medium',
                shrink: true,
                isSubmit: true,
                disabled: isLoading,
              }}
            />
          </div>
        </form>

        <hr className="border-none h-px w-full bg-grey-700" />

        <div className="space-y-3">
          <span className={sectionTitle}>Danger zone</span>
          <p className="text-text-low-priority text-sm">
            Deleting your account is permanent after 30 days. Within that
            window you can sign back in to restore access.
          </p>
          <div>
            <Button
              button={{
                text: 'Delete account',
                type: 'delete',
                size: 'medium',
                shrink: true,
                IconStart: TrashIcon,
                onClick: () => setIsDeleteOpen(true),
              }}
            />
          </div>
        </div>

        <DeleteAccountModal
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
        />
      </div>
    </>
  );
};
